<script setup lang="ts">
/**
 * Обложка сайта — первый экран над каталогом. Пять макетов из раздела «Внешний вид».
 *
 * Компонент чисто презентационный: всё приходит пропсами. Благодаря этому его же
 * рендерит превью в админке — отдельной «похожей» вёрстки для настроек не существует,
 * и разъехаться им не с чем.
 *
 * Картинка берётся из visual.banner_img: поле уже есть в БД и уже загружается
 * в «Визуальных настройках», заводить второй аплоадер незачем.
 */
import { computed } from 'vue';
import {
  COVER_HEIGHT_CSS,
  FULL_BLEED_COVER_LAYOUTS,
  HERO_HEIGHT_CSS,
  type AppearanceCoverConfig,
} from '@shared/config';

const props = withDefaults(
  defineProps<{
    cover: AppearanceCoverConfig;
    /** visual.banner_img — фон обложки. */
    imageUrl?: string | null;
    /** visual.logo_file — для макета «Только логотип». */
    logoUrl?: string | null;
    /** Название заведения — только для alt логотипа. Текст обложки им не подменяется. */
    fallbackTitle?: string;
    /** В превью админки высота ужимается, чтобы макет помещался в форму. */
    compact?: boolean;
  }>(),
  { imageUrl: '', logoUrl: '', fallbackTitle: '', compact: false },
);

/**
 * Тексты показываем ровно те, что заданы в настройках. Раньше пустой
 * заголовок подменялся названием заведения — и убрать надпись с баннера,
 * на котором она уже нарисована, было нечем.
 */
const title = computed(() => props.cover.title.trim());
const subtitle = computed(() => props.cover.subtitle.trim());
const hasText = computed(() => Boolean(title.value || subtitle.value));

const hasMedia = computed(() =>
  ['photo', 'media', 'split', 'video', 'logo', 'hero'].includes(props.cover.layout),
);
const isSplit = computed(() => props.cover.layout === 'split');

/**
 * «Только картинка» и «Баннер во весь экран»: баннер сам себе сообщение,
 * текста и затемнения на нём нет.
 */
const isMediaOnly = computed(() => ['media', 'hero'].includes(props.cover.layout));

/**
 * Растянут ли макет на всю ширину экрана.
 *
 * В превью админки (`compact`) класс не вешаем: галерея сравнивает МАКЕТЫ, и снятое
 * скругление в маленькой плитке читалось бы как поломка, а не как «во всю ширину».
 */
const isFullBleed = computed(
  () =>
    !props.compact
    && (props.cover.width === 'full' || FULL_BLEED_COVER_LAYOUTS.includes(props.cover.layout)),
);

/**
 * Фон обложки — одно поле на картинку и на видео (visual.banner_img). Тег
 * выбираем по расширению, а не по макету: если в поле фона лежит ролик,
 * показать его через <img> нельзя, и обложка осталась бы пустой.
 *
 * cover.videoUrl — легаси: когда-то видео загружалось отдельно. Оно подхватится
 * только если фон пуст, иначе новый ролик не смог бы заменить старый.
 */
const mediaUrl = computed(() => props.imageUrl || props.cover.videoUrl || '');

const isVideo = computed(() => /\.(mp4|webm)$/i.test(mediaUrl.value));

/**
 * Затемнение нужно только под текстом поверх медиа: в «половинках» текст на
 * своём фоне, а на чистой картинке затемнять нечего — она просто потускнеет.
 */
const hasOverlayContent = computed(() =>
  props.cover.layout === 'logo' ? Boolean(props.logoUrl) || hasText.value : hasText.value,
);

const showScrim = computed(
  () => hasMedia.value && !isSplit.value && !isMediaOnly.value && hasOverlayContent.value,
);

const rootStyle = computed(() => {
  // У баннера во весь экран своя лестница высот: обычная обложка делит первый экран
  // с каталогом, а он этим экраном и является
  const heights = props.cover.layout === 'hero' ? HERO_HEIGHT_CSS : COVER_HEIGHT_CSS;
  return {
    minHeight: props.compact ? '150px' : heights[props.cover.height],
    '--cover-scrim': String(props.cover.overlay / 100),
  };
});

/**
 * «Уменьшить движение» в системе — останавливаем ролик на первом кадре.
 * Прятать нельзя: обложка осталась бы пустым градиентом. Проверяем при
 * loadedmetadata, а не в setup — на сервере matchMedia нет.
 */
const onVideoReady = (event: Event) => {
  if (!globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  (event.target as HTMLVideoElement).pause();
};
</script>

<template>
  <section
    class="site-cover"
    :class="[
      `site-cover--${cover.layout}`,
      { 'site-cover--compact': compact, 'site-cover--full': isFullBleed },
    ]"
    :style="rootStyle"
  >
    <!-- Медиа -->
    <video
      v-if="hasMedia && isVideo"
      class="site-cover__media"
      :src="mediaUrl"
      autoplay
      muted
      loop
      playsinline
      preload="metadata"
      @loadedmetadata="onVideoReady"
    />
    <img v-else-if="hasMedia && mediaUrl" class="site-cover__media" :src="mediaUrl" alt="" />

    <div v-if="showScrim" class="site-cover__scrim" />

    <!-- Только логотип -->
    <div
      v-if="cover.layout === 'logo'"
      class="site-cover__content site-cover__content--center"
    >
      <img v-if="logoUrl" class="site-cover__logo" :src="logoUrl" :alt="fallbackTitle" />
      <div v-else-if="title" class="site-cover__title brand-font">{{ title }}</div>
    </div>

    <!-- Половина текст, половина фото -->
    <div v-else-if="isSplit" class="site-cover__split" :class="{ 'site-cover__split--media-only': !hasText }">
      <div v-if="hasText" class="site-cover__split-text">
        <h1 v-if="title" class="site-cover__title brand-font">{{ title }}</h1>
        <p v-if="subtitle" class="site-cover__subtitle">{{ subtitle }}</p>
      </div>
      <div class="site-cover__split-media">
        <video
          v-if="isVideo"
          :src="mediaUrl"
          autoplay
          muted
          loop
          playsinline
          preload="metadata"
          @loadedmetadata="onVideoReady"
        />
        <img v-else-if="mediaUrl" :src="mediaUrl" alt="" />
      </div>
    </div>

    <!-- Заголовок по центру: с фото, с видео или на градиенте.
         Оба поля пусты — на обложке остаётся чистая картинка. -->
    <div v-else-if="!isMediaOnly && hasText" class="site-cover__content site-cover__content--center">
      <h1 v-if="title" class="site-cover__title brand-font">{{ title }}</h1>
      <p v-if="subtitle" class="site-cover__subtitle">{{ subtitle }}</p>
    </div>
  </section>
</template>

<style scoped lang="scss">
.site-cover {
  @apply relative w-full overflow-hidden flex isolate;
  border-radius: var(--app-radius-card, 24px);
  // Градиент из акцента — фон для макета без фото и подложка, пока картинка грузится
  background:
    linear-gradient(135deg, var(--app-accent, #ef4444) 0%, rgb(0 0 0 / 0.85) 100%);

  &__media {
    @apply absolute inset-0 w-full h-full object-cover object-center;
    z-index: 0;
  }

  &__scrim {
    @apply absolute inset-0;
    z-index: 1;
    background: rgb(0 0 0 / var(--cover-scrim, 0.4));
  }

  &__content {
    @apply relative z-[2] w-full px-6 py-8 md:px-10 flex flex-col gap-3;

    &--center {
      @apply items-center justify-center text-center;
    }
  }

  &__title {
    @apply text-2xl md:text-4xl lg:text-5xl font-bold leading-tight text-white;
    text-shadow: 0 2px 12px rgb(0 0 0 / 0.45);
  }

  &__subtitle {
    @apply text-sm md:text-lg text-white/85 max-w-2xl;
  }

  &__logo {
    @apply max-h-24 md:max-h-32 w-auto object-contain;
    filter: drop-shadow(0 4px 12px rgb(0 0 0 / 0.35));
  }

  // ===== Половина текст, половина фото =====
  &__split {
    @apply relative z-[2] w-full grid grid-cols-1 md:grid-cols-2;
  }

  &__split-text {
    @apply flex flex-col justify-center gap-3 px-6 py-8 md:px-10;
  }

  // Без текста делить экран не с чем — картинка забирает всю ширину
  &__split--media-only {
    @apply md:grid-cols-1;
  }

  &__split-media {
    @apply relative min-h-[160px] md:min-h-full;

    img,
    video {
      @apply absolute inset-0 w-full h-full object-cover object-center;
    }
  }

  // В «половинках» медиа-слой не нужен — картинка живёт в своей колонке
  &--split .site-cover__media {
    @apply hidden;
  }

  // ===== На всю ширину экрана =====
  // Сама секция растянута от края до края (её выносит из контейнера pages/index.vue),
  // но текст обязан стоять по той же вертикали, что и каталог под ним, — иначе
  // заголовок уезжает к самой рамке монитора. Поэтому содержимое возвращаем
  // в те же границы и те же отступы, что у UContainer витрины.
  &--full {
    // Скругление у обложки во весь экран нечему скруглять — углы уходят за край
    border-radius: 0;

    .site-cover__content {
      @apply mx-auto max-w-[1920px] px-4 sm:px-6 lg:px-8;
    }

    .site-cover__split {
      @apply mx-auto max-w-[1920px];
    }

    .site-cover__split-text {
      @apply px-4 sm:px-6 lg:px-8;
    }
  }

  // Компактный режим для превью в админке: заголовки крупнее выглядели бы враньём
  &--compact {
    .site-cover__title {
      @apply text-lg md:text-xl;
    }

    .site-cover__subtitle {
      @apply text-xs md:text-sm;
    }

    .site-cover__logo {
      @apply max-h-12;
    }

    .site-cover__content,
    .site-cover__split-text {
      @apply px-4 py-5;
    }

    .site-cover__split-media {
      @apply min-h-[90px];
    }
  }
}

</style>
